<template>
	<div class="here-map h-100">
		<div
			:style="{ width, height }"
			class="transparent-overlay"
			v-if="!draggable && shouldRender"
		/>
		<div ref="map" :style="{ width, height }" v-if="shouldRender"/>
	</div>
</template>

<script>
import { getJSCredentials } from "@shared/Geocoder";

export default {
	props: {
		suppliers: {
			type: Array,
			required: false,
			default: () => []
		},

		value: {
			type: Object,
			required: true,
			validator(v) {
				return Object.values(v).reduce(
					(e) => e === null || typeof e === "number"
				);
			},
		},

		supplierValue: {
			type: Object,
			required: false,
			validator(v) {
				return Object.values(v).reduce(
					(e) => e === null || typeof e === "number"
				);
			},
		},

		width: {
			type: String,
			default: "100%",
		},

		height: {
			type: String,
			default: "835px",
		},

		draggable: {
			type: Boolean,
			default: false,
		},

		hideRadius: {
			type: Boolean,
			default: false,
		},

		radius: {
			type: Number,
			default: 100,
		},

		zoom: {
			type: Number,
			default: 7.65,
		},

		shouldRender: {
			type: Boolean,
			default: true,
		},

		readOnly: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		hasReverseGeocodeListener() {
			return this.$listeners && this.$listeners.reverseGeocode;
		},
	},

	watch: {
		value: {
			handler(v) {
				this.markSubject();
			},
			deep: true,
			immediate: true,
		},

		supplierValue: {
			handler(v) {
				this.markSupplier();
			},
			deep: true,
			immediate: true,
		},

		radius(v) {
			if (
				this.mapIsReady() &&
				this.isMarker(this.subject) &&
				!this.hideRadius
			) {
				this.subject.getParentGroup().forEach((child) => {
					if (this.isCircle(child)) {
						child.setRadius(v * 1000);
					}
				});
			}
		},

		draggable: {
			handler(draggable) {
				if (this.behaviorEnabled()) {
					if (draggable) {
						this.behavior.enable();
					} else {
						this.behavior.disable();
					}
				}
			},
		},
	},

	created() {
		this.map = null;
		this.behavior = null;
		this.subject = null;
		this.ui = null;
	},

	mounted() {
		if (!this.mapIsReady() && this.shouldRender) {
			const { apiKey: apikey } = getJSCredentials();
			const defaultLayers = new H.service.Platform({
				apikey,
			}).createDefaultLayers();

			this.behavior = this.makeMap(defaultLayers, apikey)
				.markSubject()
				.markSupplier()
				.allowBehavior();

			if (!this.draggable) {
				this.behavior.disable();
			}

			if (this.readOnly) {
				this.ui = H.ui.UI.createDefault(this.map, defaultLayers);
			}
		}

		var svgMarkup =
			'<svg xmlns="http://www.w3.org/2000/svg" height="48" width="48"><path d="M4 44V19.95L18 14v4l10-4.05V20h16v24Zm3-3h34V23H25v-4.6l-10 4v-3.95L7 22Zm15.5-5h3v-8h-3Zm-8 0h3v-8h-3Zm16 0h3v-8h-3ZM44 20h-9l2-16h5ZM7 41h34Z"/></svg>';

		var bearsIcon = new H.map.Icon(
			svgMarkup.replace("${FILL}", "blue").replace("${STROKE}", "red")
		);

		this.suppliers.map((supplier) => {
			const marker = new H.map.Marker(
				supplier,
				{ icon: bearsIcon }
			);
			this.map.addObject(marker);
		})
	},

	destroyed() {
		if (this.mapIsReady()) {
			this.map.dispose();
		}
	},

	methods: {
		async reverseGeocode(coords, apiKey) {
			const platform = new H.service.Platform({ apiKey });
			const service = platform.getSearchService();
			let markerAddress = {};

			await service.reverseGeocode(
				{
					at: `${coords.lat},${coords.lng},${coords.alt}`,
				},
				(result) => {
					markerAddress = result.items[0];
				},
				alert
			);

			return markerAddress;
		},

		addMarker(geometry, fromSupplier = false) {
			const icon = fromSupplier
				? this.createIcon("#4E4E4E", "#2F2F2F")
				: this.createIcon("#26B360", "#1C7A4B");

			const marker = new H.map.Marker(geometry, { icon });

			if (fromSupplier) {
				this.map.addObject(marker);
			} else {
				const circle = new H.map.Circle(
					marker.getGeometry(),
					this.radius * 1000,
					{
						visibility: !this.hideRadius,
					}
				);

				const group = new H.map.Group({
					objects: [circle, marker],
				});

				marker.addEventListener("move", (e) =>
					circle.setCenter(e.target.getGeometry())
				);
				group.addOnDisposeCallback(() => marker.dispose());

				this.map.addObject(group);
			}

			this.addTooltip(marker, fromSupplier);
			return marker;
		},

		markSubject() {
			if (!this.mapIsReady()) {
				return this;
			}

			let currentCoords = this.valueIsValid(this.value)
				? this.value
				: {
					lat: -35.30655,
					lng: 149.12656,
				};

			this.subject = this.clearSubject().addMarker(currentCoords);

			this.map
				.getViewModel()
				.setLookAtData(this.markerScreenToLookAtData(this.subject), true);

			return this;
		},

		markSupplier() {
			if (!this.mapIsReady()) {
				return this;
			}

			if (this.valueIsValid(this.supplierValue)) this.addMarker(this.supplierValue, true);
			return this;
		},

		async dragEndEventListener(e, apiKey) {
			if (this.isMarker(this.subject)) {
				this.$emit("change", this.subject.getGeometry());

				if (this.hasReverseGeocodeListener) {
					const markerAddresss = await this.reverseGeocode(
						this.subject.getGeometry(),
						apiKey
					);
					this.$emit("reverseGeocode", markerAddresss);
				}
			}
		},

		mapViewChangeEventListener({ newValue, oldValue }) {
			try {
				if (newValue.lookAt.zoom === oldValue.lookAt.zoom) return;

				const viewport = this.markerScreenToLookAtData(this.subject);
				this.map.getViewModel().setLookAtData(viewport, true);
			} catch {}
		},

		dragEventListener(e) {
			if (this.isMarker(this.subject) && !this.readOnly) {
				this.subject.setGeometry(this.map.getCenter()).dispatchEvent("move");
			}
		},

		resizeMapViewPortEventListener(e) {
			try {
				this.map.getViewPort().resize();
			} catch {}
		},

		clearSubject() {
			if (this.isMarker(this.subject)) {
				//this.map.removeObject(this.subject.getParentGroup())
				this.subject.getParentGroup().dispose();
			}

			return this;
		},

		allowBehavior() {
			if (this.behaviorEnabled()) return;

			const { PANNING, PINCH_ZOOM, WHEEL_ZOOM, FRACTIONAL_ZOOM } =
				H.mapevents.Behavior.Feature;

			return new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map), {
				kinetics: {
					duration: 1,
				},
				enabled: PANNING | PINCH_ZOOM | WHEEL_ZOOM | FRACTIONAL_ZOOM,
			});
		},

		registerMapEventListeners(apiKey) {
			this.map.addEventListener(
				"drag", 
				this.dragEventListener, 
				false
			);
			
			this.map.addEventListener(
				"dragend",
				(e) => this.dragEndEventListener(e, apiKey),
				false
			);

			this.map.addEventListener(
				"mapviewchange",
				this.mapViewChangeEventListener,
				false
			);

			window.addEventListener(
				"resize", 
				this.resizeMapViewPortEventListener
			);

			this.map.addOnDisposeCallback((e) => {
				window.removeEventListener(
					"resize",
					this.resizeMapViewPortEventListener
				);
			});
		},

		makeMap(defaultLayers, apiKey) {
			if (this.mapIsReady()) this.map.dispose();

			let currentCoords = this.valueIsValid(this.value)
				? this.value
				: {
					lat: -35.30655,
					lng: 149.12656,
				};

			this.map = new H.Map(this.$refs.map, defaultLayers.vector.normal.map, {
				zoom: this.zoom,
				pixelRatio: window.devicePixelRatio || 1,
				center: currentCoords,
			});

			this.registerMapEventListeners(apiKey);

			return this;
		},

		markerScreenToLookAtData(marker) {
			if (!this.isMarker(marker) || !this.mapIsReady()) return;

			const { x, y } = this.map.geoToScreen(marker.getGeometry());
			return this.map.screenToLookAtData(x, y);
		},

		behaviorEnabled() {
			return this.behavior instanceof H.mapevents.Behavior;
		},

		valueIsValid(coords) {
			return (
				coords &&
				Number.isFinite(coords.lat) &&
				Number.isFinite(coords.lng)
			);
		},

		mapIsReady() {
			return this.map instanceof H.Map;
		},

		isMarker(object) {
			return object instanceof H.map.Marker;
		},

		isCircle(object) {
			return object instanceof H.map.Circle;
		},

		createIcon(fillColor, strokeColor) {
			const svgTemplate = `
				<svg xmlns="http://www.w3.org/2000/svg" width="48" height="72" viewBox="0 0 24 36">
					<path d="M12 0C7 0 3 4 3 9c0 7 9 18 9 18s9-11 9-18c0-5-4-9-9-9zm0 12.5a3.5 3.5 0 110-7 3.5 3.5 0 010 7z"
						fill="${fillColor}" stroke="${strokeColor}" stroke-width="1"/>
				</svg>
			`.trim();

			const encoded = encodeURIComponent(svgTemplate)
				.replace(/'/g, '%27')
				.replace(/"/g, '%22');

			const dataUrl = `data:image/svg+xml;charset=UTF-8,${encoded}`;
  			return new H.map.Icon(dataUrl);
		},

		addTooltip(marker, fromSupplier) {
			// Tooltip content
			const tooltipText = fromSupplier
				? "Trade Service Center"
				: "Site Address";

			// Hover (pointerenter) event
			marker.addEventListener('pointerenter', evt => {
				const position = marker.getGeometry();

				const bubble = new H.ui.InfoBubble(position, {
					content: `<div style="font-size: 14px;">${tooltipText}</div>`
				});

				// Store the bubble so we can remove it later
				marker.bubble = bubble;

				this.ui.addBubble(bubble);
			});

			// Remove tooltip on pointer leave
			marker.addEventListener('pointerleave', () => {
				if (marker.bubble) {
					this.ui.removeBubble(marker.bubble);
					marker.bubble = null;
				}
			});
		}
	},
};
</script>

<style lang="scss" scoped>
.transparent-overlay {
  position: absolute;
  z-index: 9;
  opacity: 0;
  width: calc(100% - 1.6rem) !important;
  margin-left: 0;
}
</style>
